/**
 * Validation utilities for legacy node and endpoint structures
 */
import {
  REQUIRED_FIELD_VALUES,
  type RequiredFieldValidationOptions,
} from './constants/index'
import type { LegacyEndpointUuid } from './legacyEndpoints'
import { isValidLegacyEndpointUuid } from './legacyEndpoints'
import type { LegacyFormatNode } from './legacyFormatTypes'
import type { LegacyBaseNode, LegacyNode, LegacyStartNode } from './legacyNodes'
import type { LegacyNodeKind } from './legacyNodes'
import { extractNodeIdFromUuid } from './uuidUtils'

/**
 * Validates that a legacy node has all required fields
 */
export function validateLegacyNode(node: LegacyNode): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!node.top || typeof node.top !== 'string') {
    errors.push('Missing or invalid "top" field')
  }
  if (!node.left || typeof node.left !== 'string') {
    errors.push('Missing or invalid "left" field')
  }
  if (!node.width || typeof node.width !== 'string') {
    errors.push('Missing or invalid "width" field')
  }
  if (!node.height || typeof node.height !== 'string') {
    errors.push('Missing or invalid "height" field')
  }
  if (!node.id || typeof node.id !== 'string') {
    errors.push('Missing or invalid "id" field')
  }
  if (!node.kind || typeof node.kind !== 'string') {
    errors.push('Missing or invalid "kind" field')
  }

  if (!node.endpoints) {
    errors.push('Missing "endpoints" object')
  } else {
    if (!Array.isArray(node.endpoints.sources)) {
      errors.push('Missing or invalid "endpoints.sources" array')
    }
    if (!Array.isArray(node.endpoints.targets)) {
      errors.push('Missing or invalid "endpoints.targets" array')
    }
  }

  if (node.kind !== 'start_node') {
    const baseNode = node as LegacyBaseNode
    if (!('html' in baseNode)) {
      errors.push(
        'Missing "html" property (required for all nodes except start_node)',
      )
    }
  } else {
    const startNode = node as LegacyStartNode
    if ('html' in startNode) {
      errors.push('start_node should not have "html" property')
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Validates endpoint UUID format
 */
export function validateEndpointUuid(uuid: string): boolean {
  return isValidLegacyEndpointUuid(uuid)
}

/**
 * Validates that extracted node ID from UUID matches the target node's ID
 * Legacy extracts target node ID from target_uuid using: target_uuid.split('-')[1].split('_')[0]
 * This extracted ID MUST match the target node's id field exactly
 */
export function validateNodeIdMatch(
  targetUuid: LegacyEndpointUuid,
  targetNodeId: string,
): {
  valid: boolean
  extractedNodeId: string | null
  error?: string
} {
  const extractedNodeId = extractNodeIdFromUuid(targetUuid)
  if (!extractedNodeId) {
    return {
      valid: false,
      extractedNodeId: null,
      error: `Could not extract node ID from UUID: ${targetUuid}`,
    }
  }
  if (extractedNodeId !== targetNodeId) {
    return {
      valid: false,
      extractedNodeId,
      error: `Node ID mismatch: extracted "${extractedNodeId}" from UUID, but target node has id "${targetNodeId}"`,
    }
  }
  return {
    valid: true,
    extractedNodeId,
  }
}

/**
 * Validates that a target endpoint exists for a given target_uuid
 * Every target_uuid in a source endpoint MUST have a corresponding target endpoint
 * in the target node's endpoints.targets[] array with that exact UUID
 */
export function validateTargetEndpointExists(
  targetUuid: LegacyEndpointUuid,
  targetNodeEndpoints: LegacyNode['endpoints'] | LegacyFormatNode['endpoints'],
): boolean {
  if (!targetNodeEndpoints?.targets) {
    return false
  }
  return targetNodeEndpoints.targets.some(
    (target) => target.uuid === targetUuid,
  )
}

/**
 * Validates all endpoint UUID references in a node
 */
export function validateEndpointReferences(node: LegacyNode): {
  valid: boolean
  errors: Array<{
    type: 'source' | 'target'
    uuid: string
    referencedUuid: string
    error: string
  }>
} {
  const errors: Array<{
    type: 'source' | 'target'
    uuid: string
    referencedUuid: string
    error: string
  }> = []

  // Validate source endpoints
  // Handle both array format [{}] and object format {} (defensive)
  const sources = Array.isArray(node.endpoints?.sources)
    ? node.endpoints.sources
    : node.endpoints?.sources
      ? [node.endpoints.sources]
      : []
  sources.forEach((source) => {
    if (source.uuid && !validateEndpointUuid(source.uuid)) {
      errors.push({
        type: 'source',
        uuid: source.uuid,
        referencedUuid: source.target_uuid || '',
        error: `Invalid source endpoint UUID format: ${source.uuid}`,
      })
    }
    if (source.target_uuid && !validateEndpointUuid(source.target_uuid)) {
      errors.push({
        type: 'source',
        uuid: source.uuid || '',
        referencedUuid: source.target_uuid,
        error: `Invalid target_uuid format: ${source.target_uuid}`,
      })
    }
  })

  // Validate target endpoints
  // Handle both array format and object format (defensive)
  const targets = Array.isArray(node.endpoints?.targets)
    ? node.endpoints.targets
    : node.endpoints?.targets
      ? [node.endpoints.targets]
      : []
  targets.forEach((target) => {
    if (target.uuid && !validateEndpointUuid(target.uuid)) {
      errors.push({
        type: 'target',
        uuid: target.uuid,
        referencedUuid: target.source_uuid || '',
        error: `Invalid target endpoint UUID format: ${target.uuid}`,
      })
    }
    if (target.source_uuid && !validateEndpointUuid(target.source_uuid)) {
      errors.push({
        type: 'target',
        uuid: target.uuid || '',
        referencedUuid: target.source_uuid,
        error: `Invalid source_uuid format: ${target.source_uuid}`,
      })
    }
  })

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Validation error for required field values
 */
export interface RequiredFieldValidationError {
  nodeId: string
  nodeKind: LegacyNodeKind
  nodeLabel?: string
  field: string
  message: string
  messageKey?: string
}

/**
 * Validates that all required field values are present and valid
 * @param nodes - Array of legacy format nodes to validate
 * @returns Validation result with errors array
 */
export function validateRequiredFieldValues(
  nodes: LegacyFormatNode[],
  options: RequiredFieldValidationOptions = {},
): {
  valid: boolean
  errors: RequiredFieldValidationError[]
} {
  const errors: RequiredFieldValidationError[] = []

  for (const node of nodes) {
    // Skip start_node as it has no HTML fields
    if (node.kind === 'start_node') {
      continue
    }

    const validators = REQUIRED_FIELD_VALUES[node.kind as LegacyNodeKind]
    if (!validators || validators.length === 0) {
      continue // No required fields for this node type
    }

    const html = node.html || {}
    const nodeLabel = node.kind.replace(/_/g, ' ').toUpperCase()

    for (const validator of validators) {
      const value = html[validator.field]
      const isValid = validator.validator(value, html, options)

      if (!isValid) {
        errors.push({
          nodeId: node.id,
          nodeKind: node.kind as LegacyNodeKind,
          nodeLabel,
          field: validator.field,
          message: validator.errorMessage,
          messageKey: validator.errorKey,
        })
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
